import {
  createEntityAdapter,
  createSlice,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit'

import { OpportunityNote } from '@user/components/Notes/typings'
import { StoreData } from '../reducers/typings'

import {
  createOpportunityNote,
  deleteOpportunityNote,
  getOpportunityNotes,
  getOpportunityNoteById,
  updateOpportunityNote,
} from './api'

const opportunityNotesAdapter = createEntityAdapter<OpportunityNote>()

type OpportunityNotesState = {
  count: number
  isLoading: boolean
  listIsStale: boolean
  notes: EntityState<OpportunityNote>
  opportunityId: number | null
  page: number
  skip: number
  take: number
}

export const initialState: OpportunityNotesState = {
  count: 0,
  isLoading: false,
  listIsStale: false,
  notes: opportunityNotesAdapter.getInitialState(),
  opportunityId: null,
  page: 1,
  skip: 0,
  take: 0,
}

export const opportunityNoteSlice = createSlice({
  name: 'opportunityNotes',
  initialState,
  reducers: {
    setPage: (state: OpportunityNotesState, action: PayloadAction<number>) => {
      state.page = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getOpportunityNotes.pending, (state) => {
      opportunityNotesAdapter.removeAll(state.notes)
      state.isLoading = true
    }),
      builder.addCase(getOpportunityNotes.fulfilled, (state, { payload }) => {
        opportunityNotesAdapter.addMany(state.notes, payload.items || [])
        state.count = payload.count
        state.isLoading = false
        state.listIsStale = false
        state.opportunityId = payload.opportunityId
        state.skip = payload.skip
        state.take = payload.take
      }),
      builder.addCase(getOpportunityNoteById.pending, (state) => {
        state.isLoading = true
      }),
      builder.addCase(
        getOpportunityNoteById.fulfilled,
        (state, { payload }) => {
          opportunityNotesAdapter.upsertOne(state.notes, payload)
          state.isLoading = false
        },
      ),
      builder.addCase(deleteOpportunityNote.pending, (state) => {
        state.isLoading = true
      }),
      builder.addCase(deleteOpportunityNote.fulfilled, (state, { payload }) => {
        opportunityNotesAdapter.removeOne(state.notes, payload)
        state.count = state.count - 1
        state.isLoading = false
        state.listIsStale = true
      }),
      builder.addCase(createOpportunityNote.pending, (state) => {
        state.isLoading = true
      }),
      builder.addCase(createOpportunityNote.fulfilled, (state, { payload }) => {
        opportunityNotesAdapter.addOne(state.notes, payload)
        state.count = state.count + 1
        state.isLoading = false
        state.listIsStale = true
      }),
      builder.addCase(updateOpportunityNote.pending, (state) => {
        state.isLoading = true
      }),
      builder.addCase(updateOpportunityNote.fulfilled, (state, { payload }) => {
        opportunityNotesAdapter.upsertOne(state.notes, payload)
        state.isLoading = false
      })
  },
})

export default opportunityNoteSlice.reducer

// SELECTORS
export const opportunityNoteSelectors =
  opportunityNotesAdapter.getSelectors<StoreData>(
    (state) => state.opportunityNotes.notes,
  )

export const selectOpportunityNoteSlice = (state: StoreData) => {
  return state.opportunityNotes
}

export const {
  selectById: selectOpportunityNoteById,
  selectAll: selectAllOpportunityNotes,
} = opportunityNoteSelectors

export const { setPage } = opportunityNoteSlice.actions
